import React from 'react';
import { Box, Typography, Paper, Grid, Stack, Divider, useTheme, useMediaQuery } from '@mui/material';
import SectionTitle from './SectionTitle';
import MatchStub from './MatchStub';

const TournamentBracket = ({ matches, type }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const renderSingleEliminationBracket = () => {
    // Group matches by round
    const rounds = matches.reduce((acc, match) => {
      if (!acc[match.round]) {
        acc[match.round] = [];
      }
      acc[match.round].push(match);
      return acc;
    }, {});

    // Sort rounds by number
    const sortedRounds = Object.entries(rounds).sort(([a], [b]) => parseInt(a) - parseInt(b));

    return (
      <SectionTitle title="Bracket del Torneo" subtitle="Eliminación Simple">
        <Box sx={{ 
          overflowX: 'auto',
          py: 2,
          mx: -1.8, // Compensate for padding in SectionTitle
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}>
          <Box sx={{ 
            minWidth: 'max-content', 
            px: 3, 
            pb: 2,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: 4
          }}>
            {sortedRounds.map(([round, roundMatches], roundIndex) => (
              <Box key={round} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    textAlign: 'center', 
                    mb: 1, 
                    color: '#c89e38',
                    fontWeight: 'bold',
                    fontSize: '0.85rem'
                  }}
                >
                  {round === '1' ? 'Primera Ronda' : 
                   round === '2' ? 'Semifinales' : 
                   round === '3' ? 'Final' : `Ronda ${round}`}
                </Typography>
                <Stack 
                  spacing={roundIndex === 0 ? 2 : roundIndex === 1 ? 4 : 8} 
                  sx={{ 
                    '& > div': {
                      marginTop: '0 !important',  // Override MUI default spacing
                      marginBottom: '0 !important',
                    }
                  }}
                >
                  {roundMatches.map((match, matchIndex) => (
                    <Box 
                      key={match.id} 
                      sx={{ 
                        position: 'relative',
                        my: roundIndex === 0 ? 1 : roundIndex === 1 ? 2 : 4
                      }}
                    >
                      {/* Connecting lines */}
                      {roundIndex < sortedRounds.length - 1 && (
                        <>
                          <Box
                            sx={{
                              position: 'absolute',
                              right: -32,
                              top: '50%',
                              width: 32,
                              height: 2,
                              bgcolor: 'rgba(200, 158, 56, 0.3)',
                              zIndex: 1
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              right: -32,
                              top: matchIndex % 2 === 0 ? '50%' : '0%',
                              width: 2,
                              height: matchIndex % 2 === 0 ? '50%' : '100%',
                              bgcolor: 'rgba(200, 158, 56, 0.3)',
                              zIndex: 1
                            }}
                          />
                        </>
                      )}
                      <MatchStub match={match} position={matchIndex} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </SectionTitle>
    );
  };

  const renderDoubleEliminationBracket = () => {
    // Separate matches by bracket
    const winnersBracket = matches.filter(m => m.bracket === 'winners');
    const losersBracket = matches.filter(m => m.bracket === 'losers');
    const grandFinal = matches.filter(m => m.bracket === 'grand_final');

    // Group matches by round
    const groupMatchesByRound = (matches) => {
      return matches.reduce((acc, match) => {
        if (!acc[match.round]) {
          acc[match.round] = [];
        }
        acc[match.round].push(match);
        return acc;
      }, {});
    };

    const winnersRounds = groupMatchesByRound(winnersBracket);
    const losersRounds = groupMatchesByRound(losersBracket);

    return (
      <SectionTitle title="Bracket del Torneo" subtitle="Doble Eliminación">
        <Box sx={{ 
          overflowX: 'auto',
          py: 2,
          mx: -1.8, // Compensate for padding in SectionTitle
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}>
          <Box sx={{ 
            minWidth: 'max-content', 
            px: 3, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: 3
          }}>
            {/* Winners Bracket */}
            <Box sx={{ width: '100%', mb: 3 }}>
              <Typography 
                variant="subtitle1" 
                sx={{
                  color: '#c89e38',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  mb: 1
                }}
              >
                Bracket de Ganadores
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: 'block',
                  mb: 1.5
                }}
              >
                {winnersBracket.length} partidas
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                {Object.entries(winnersRounds).map(([round, roundMatches], roundIndex) => (
                  <Box key={round} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 1,
                        textAlign: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      Ronda {round}
                    </Typography>
                    <Stack spacing={isMobile ? 1 : 2}>
                      {roundMatches.map((match, matchIndex) => (
                        <Box key={match.id} sx={{ position: 'relative', mb: 2 }}>
                          {/* Connecting lines */}
                          {roundIndex < Object.keys(winnersRounds).length - 1 && (
                            <>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -32,
                                  top: '50%',
                                  width: 32,
                                  height: 2,
                                  bgcolor: 'rgba(200, 158, 56, 0.3)',
                                  zIndex: 1
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -32,
                                  top: matchIndex % 2 === 0 ? '50%' : '0%',
                                  width: 2,
                                  height: matchIndex % 2 === 0 ? '50%' : '100%',
                                  bgcolor: 'rgba(200, 158, 56, 0.3)',
                                  zIndex: 1
                                }}
                              />
                            </>
                          )}
                          <MatchStub match={match} position={matchIndex} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Losers Bracket */}
            <Box sx={{ width: '100%', mb: 3 }}>
              <Typography 
                variant="subtitle1" 
                sx={{
                  color: '#c89e38',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  mb: 1
                }}
              >
                Bracket de Perdedores
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: 'block',
                  mb: 1.5
                }}
              >
                {losersBracket.length} partidas
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                {Object.entries(losersRounds).map(([round, roundMatches], roundIndex) => (
                  <Box key={round} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 1,
                        textAlign: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      Ronda {round}
                    </Typography>
                    <Stack spacing={isMobile ? 1 : 2}>
                      {roundMatches.map((match, matchIndex) => (
                        <Box key={match.id} sx={{ position: 'relative', mb: 2 }}>
                          {/* Connecting lines */}
                          {roundIndex < Object.keys(losersRounds).length - 1 && (
                            <>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -32,
                                  top: '50%',
                                  width: 32,
                                  height: 2,
                                  bgcolor: 'rgba(200, 158, 56, 0.3)',
                                  zIndex: 1
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -32,
                                  top: matchIndex % 2 === 0 ? '50%' : '0%',
                                  width: 2,
                                  height: matchIndex % 2 === 0 ? '50%' : '100%',
                                  bgcolor: 'rgba(200, 158, 56, 0.3)',
                                  zIndex: 1
                                }}
                              />
                            </>
                          )}
                          <MatchStub match={match} position={matchIndex} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Grand Final */}
            {grandFinal.length > 0 && (
              <Box sx={{ width: '100%' }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{
                    color: '#c89e38',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    mb: 1
                  }}
                >
                  Gran Final
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    display: 'block',
                    mb: 1.5
                  }}
                >
                  Partida final
                </Typography>
                <Stack spacing={2}>
                  {grandFinal.map((match) => (
                    <Box 
                      key={match.id} 
                      sx={{ 
                        width: '100%', 
                        maxWidth: '300px',
                        mx: 'auto'
                      }}
                    >
                      <MatchStub match={match} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </SectionTitle>
    );
  };

  const renderRoundRobinTable = () => {
    // Create a table of all participants and their results
    const participants = [...new Set(matches.flatMap(m => [m.player1, m.player2]))];
    const results = participants.map(player => ({
      player,
      wins: matches.filter(m => m.winner === player).length,
      losses: matches.filter(m => m.player1 === player || m.player2 === player).length -
        matches.filter(m => m.winner === player).length
    }));

    // Sort by wins
    results.sort((a, b) => b.wins - a.wins);

    return (
      <SectionTitle title="Tabla de Posiciones" subtitle="Todos contra Todos">
        <Box sx={{ 
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
          }}>
            <thead>
              <tr style={{
                backgroundColor: 'rgba(200, 158, 56, 0.1)'
              }}>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left',
                  color: '#c89e38',
                  fontWeight: 'bold',
                  borderBottom: '2px solid rgba(200, 158, 56, 0.3)'
                }}>Jugador</th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'center',
                  color: '#c89e38',
                  fontWeight: 'bold',
                  borderBottom: '2px solid rgba(200, 158, 56, 0.3)'
                }}>Victorias</th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'center',
                  color: '#c89e38',
                  fontWeight: 'bold',
                  borderBottom: '2px solid rgba(200, 158, 56, 0.3)'
                }}>Derrotas</th>
                <th style={{ 
                  padding: '12px 16px', 
                  textAlign: 'center',
                  color: '#c89e38',
                  fontWeight: 'bold',
                  borderBottom: '2px solid rgba(200, 158, 56, 0.3)'
                }}>Win %</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr 
                  key={result.player}
                  style={{
                    backgroundColor: index === 0 ? 'rgba(200, 158, 56, 0.05)' : 'transparent'
                  }}
                >
                  <td style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #eee',
                    fontWeight: index === 0 ? 'bold' : 'normal'
                  }}>
                    {result.player}
                    {index === 0 && (
                      <span style={{ 
                        marginLeft: '8px',
                        color: '#c89e38',
                        fontSize: '0.75rem'
                      }}>
                        👑 Líder
                      </span>
                    )}
                  </td>
                  <td style={{ 
                    padding: '12px 16px', 
                    textAlign: 'center', 
                    borderBottom: '1px solid #eee',
                    fontWeight: 'bold',
                    color: index === 0 ? '#c89e38' : 'inherit'
                  }}>
                    {result.wins}
                  </td>
                  <td style={{ 
                    padding: '12px 16px', 
                    textAlign: 'center', 
                    borderBottom: '1px solid #eee' 
                  }}>
                    {result.losses}
                  </td>
                  <td style={{ 
                    padding: '12px 16px', 
                    textAlign: 'center', 
                    borderBottom: '1px solid #eee',
                    color: index === 0 ? '#c89e38' : 'inherit' 
                  }}>
                    {result.wins + result.losses > 0 
                      ? `${Math.round((result.wins / (result.wins + result.losses)) * 100)}%` 
                      : '0%'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </SectionTitle>
    );
  };

  if (!matches || matches.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          textAlign: 'center'
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No hay partidas programadas aún.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {type === 'round_robin' ? renderRoundRobinTable() : 
       type === 'double_elimination' ? renderDoubleEliminationBracket() : 
       renderSingleEliminationBracket()}
    </Box>
  );
};

export default TournamentBracket; 